import React, { useState , useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import swal from '@sweetalert/with-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle, faChevronDown, faCheck, faTimes, faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { faClone } from '@fortawesome/free-regular-svg-icons';

import { isEmpty, useOutsideClick } from '../js/Helpers';

export default function Navbar({ schoolType, schoolInfo, schoolInfoLoading, userInfo, userInfoLoading, scheduleInfo, scheduleInfoLoading, loadScreen, setLoadScreen, handleSwitchCurrentYear, handleSwitchCurrentScheduleVersion, handleDuplicateScheduleVersion, handleDeleteScheduleVersion, handleUpdateScheduleVersionName, handleLogout }) {
	const location = useLocation();
	const current_year_ref = useRef();
	const current_schedule_version_ref = useRef();
	const schedule_name_input_ref = useRef();
	const schedule_name_click_timeout_ref = useRef(null);
	const settings_ref = useRef();
	
	const [isLoading, setIsLoading] = useState(true);
	
	const [currentYearVisible, setCurrentYearVisible] = useState(false);
	const [currentScheduleVersionVisible, setCurrentScheduleVersionVisible] = useState(false);
	const [editingScheduleVersionID, setEditingScheduleVersionID] = useState(null);
	const [editingScheduleName, setEditingScheduleName] = useState('');
	const [settingsVisible, setSettingsVisible] = useState(false);
	const dropdownOutsideClickExclusions = ['header-dropdown-content-floating-container'];
	const isDataScreen = (location.pathname.includes('/school_data') || location.pathname.includes('/scheduling_data'));
	const hasSidebarLayout = (isDataScreen || location.pathname.includes('/progress'));
	const shouldFloatHeaderDropdown = hasSidebarLayout || location.pathname.includes('/magnetboard');
	
	const handleIconClick = (click_type) => {
		if(click_type === 'current_year')
		{
			setCurrentYearVisible(!currentYearVisible);
			setCurrentScheduleVersionVisible(false);
			setSettingsVisible(false);
		}
		else if(click_type === 'current_schedule_version')
		{
			setCurrentScheduleVersionVisible(!currentScheduleVersionVisible);
			setCurrentYearVisible(false);
			setSettingsVisible(false);
		}
		else if(click_type === 'settings')
		{
			setSettingsVisible(!settingsVisible);
			setCurrentYearVisible(false);
			setCurrentScheduleVersionVisible(false);
		}
	}
	
	useOutsideClick(settings_ref, () => {
		setSettingsVisible(false);
	}, dropdownOutsideClickExclusions);
	
	useOutsideClick(current_year_ref, () => {
		setCurrentYearVisible(false);
	}, dropdownOutsideClickExclusions);

	useOutsideClick(current_schedule_version_ref, () => {
		setCurrentScheduleVersionVisible(false);
	}, dropdownOutsideClickExclusions);
	
	const signOut = () => {
		sessionStorage.clear();
		localStorage.clear();
		
		handleLogout();
	}
	
	//////////////////////////////
	///// DO ON STATE CHANGE /////
	//////////////////////////////
	useEffect(() => {
		if(!schoolInfoLoading && !userInfoLoading && !scheduleInfoLoading) setIsLoading(false);
	}, [schoolInfoLoading, userInfoLoading, scheduleInfoLoading]);

	useEffect(() => {
		if(!editingScheduleVersionID || !schedule_name_input_ref.current) return;

		schedule_name_input_ref.current.focus();
		schedule_name_input_ref.current.select();
	}, [editingScheduleVersionID]);

	useEffect(() => {
		if(currentScheduleVersionVisible) return;

		setEditingScheduleVersionID(null);
		setEditingScheduleName('');
	}, [currentScheduleVersionVisible]);

	useEffect(() => {
		return () => {
			if(schedule_name_click_timeout_ref.current) clearTimeout(schedule_name_click_timeout_ref.current);
		};
	}, []);
	
	var currentYearOptionsVisible = currentYearVisible ? 'fadeIn' : 'fadeOut';
	var currentScheduleVersionOptionsVisible = currentScheduleVersionVisible ? 'fadeIn' : 'fadeOut';
	var settingOptionsVisible = settingsVisible ? 'fadeIn' : 'fadeOut';
	
	const app_current_schedule_year_index = ('schedule_years' in scheduleInfo) ? scheduleInfo.schedule_years.findIndex(schedule_year => schedule_year.schedule_year_id === scheduleInfo.app_current_schedule_year_id) : null;
	const current_schedule_year = (('schedule_years' in scheduleInfo) && schoolInfo.current_schedule_year_id) ? scheduleInfo.schedule_years.find(schedule_year => schedule_year.schedule_year_id === schoolInfo.current_schedule_year_id).schedule_year : null;
	const current_schedule_year_info = (('schedule_years' in scheduleInfo) && schoolInfo.current_schedule_year_id) ? scheduleInfo.schedule_years.find(schedule_year => `${schedule_year.schedule_year_id}` === `${schoolInfo.current_schedule_year_id}`) : null;
	const schedules_from_current_schedule_year = (('schedules' in scheduleInfo) && schoolInfo.current_schedule_year_id) ? scheduleInfo.schedules.filter(schedule => `${schedule.schedule_year_id}` === `${schoolInfo.current_schedule_year_id}`) : [];
	const current_schedule_version_info = schedules_from_current_schedule_year.find(schedule => `${schedule.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);
	const current_schedule_version = (current_schedule_version_info) ? current_schedule_version_info.schedule_name : null;
	const can_delete_schedule_versions = schedules_from_current_schedule_year.length > 1;
	const can_duplicate_schedule_versions = `${schoolInfo.current_schedule_year_id}` === `${scheduleInfo.app_current_schedule_year_id}`;
	const can_manage_schedule_versions = userInfo && (userInfo.role_id === '1' || userInfo.role_id === '2');
	const max_schedules_allowed = (current_schedule_year_info && current_schedule_year_info.num_schedules_allowed) ? parseInt(current_schedule_year_info.num_schedules_allowed, 10) : 5;
	const duplicate_schedule_limit_reached = schedules_from_current_schedule_year.length >= max_schedules_allowed;

	const getContentSafeLeftBoundary = () => {
		if(typeof document === 'undefined' || !hasSidebarLayout) return 0;

		const content_selectors = ['#school-data-main-content', '#progress-page-main-content'];
		const content_left_boundary = content_selectors.reduce((current_max, selector) => {
			const content_container = document.querySelector(selector);
			if(!content_container) return current_max;

			const content_rect = content_container.getBoundingClientRect();
			if(content_rect.width <= 0) return current_max;

			return Math.max(current_max, content_rect.left);
		}, 0);

		return content_left_boundary > 0 ? content_left_boundary + 16 : 0;
	};

	const startScheduleNameEdit = (e, schedule_version_info) => {
		e.stopPropagation();
		if(!can_manage_schedule_versions) return;
		if(schedule_name_click_timeout_ref.current) clearTimeout(schedule_name_click_timeout_ref.current);

		setEditingScheduleVersionID(schedule_version_info.schedule_version_id);
		setEditingScheduleName(schedule_version_info.schedule_name || '');
	}

	const handleScheduleNameClick = (e, schedule_version_info, option_selected) => {
		e.stopPropagation();
		if(option_selected) return;

		if(schedule_name_click_timeout_ref.current) clearTimeout(schedule_name_click_timeout_ref.current);
		schedule_name_click_timeout_ref.current = setTimeout(() => {
			handleSwitchCurrentScheduleVersion({
				school_id: schoolInfo.school_id,
				selected_schedule_version_id: schedule_version_info.schedule_version_id,
				selected_schedule_year_id: schedule_version_info.schedule_year_id
			});
			schedule_name_click_timeout_ref.current = null;
		}, 175);
	}

	const cancelScheduleNameEdit = (e) => {
		if(e) e.stopPropagation();

		setEditingScheduleVersionID(null);
		setEditingScheduleName('');
	}

	const saveScheduleNameEdit = async (e) => {
		if(e) e.stopPropagation();
		if(!can_manage_schedule_versions) return;

		const updated_schedule_name = editingScheduleName.trim();
		if(!updated_schedule_name) return;

		const response = await handleUpdateScheduleVersionName({
			schedule_version_id: editingScheduleVersionID,
			schedule_name: updated_schedule_name
		});

		if(response && response.success)
		{
			setEditingScheduleVersionID(null);
			setEditingScheduleName('');
		}
	}

	const confirmDeleteSchedule = async (e, schedule_version_info) => {
		e.stopPropagation();
		if(!can_delete_schedule_versions || !handleDeleteScheduleVersion) return;

		const options = {
			title: "Are you sure?",
			text: "This will delete this schedule version. This action cannot be undone.",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Delete Schedule",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
		};

		const confirmed = await swal(options);
		if(!confirmed) return;

		handleDeleteScheduleVersion({
			school_id: schoolInfo.school_id,
			schedule_version_id: schedule_version_info.schedule_version_id,
			schedule_year_id: schedule_version_info.schedule_year_id
		});
	}

	const getFloatingDropdownStyle = (trigger_ref, dropdown_style = {}) => {
		if(!trigger_ref.current || typeof window === 'undefined') return dropdown_style;

		const trigger_rect = trigger_ref.current.getBoundingClientRect();
		const dropdown_width = parseInt((dropdown_style.width || dropdown_style.maxWidth || 300), 10) || 300;
		const min_dropdown_left = getContentSafeLeftBoundary();
		const min_dropdown_right = min_dropdown_left + dropdown_width;
		const max_dropdown_right = window.innerWidth - 20;
		const dropdown_right = Math.min(max_dropdown_right, Math.max(trigger_rect.right, min_dropdown_right));

		return {
			...dropdown_style,
			position: 'fixed',
			top: `${trigger_rect.bottom + 2}px`,
			left: `${dropdown_right}px`,
			right: 'auto',
			transform: 'translateX(-100%)',
			zIndex: 10050
		};
	}

	const renderHeaderDropdown = (trigger_ref, visible_class_name, dropdown_style, children) => {
		if(shouldFloatHeaderDropdown && typeof document !== 'undefined')
		{
			return createPortal(
				<div className={`header-dropdown-content ${visible_class_name} header-dropdown-content-floating parent-div header-dropdown-content-floating-container`} style={getFloatingDropdownStyle(trigger_ref, dropdown_style)}>
					{children}
				</div>,
				document.body
			);
		}

		return (
			<div className={`header-dropdown-content ${visible_class_name}`} style={dropdown_style}>
				{children}
			</div>
		);
	}
	
	return (
		<div className={`header-container ${hasSidebarLayout ? 'header-container-data-screen' : ''}`}>
			<div className='header-schedule-selectors-container'>
				<div className='header-current-year-container' onClick={() => handleIconClick('current_year')} ref={current_year_ref}>
					<div>{isEmpty(scheduleInfo) ? '--' : current_schedule_year}</div>
					<FontAwesomeIcon className='header-current-year-arrow' icon={faChevronDown} />
					{renderHeaderDropdown(current_year_ref, currentYearOptionsVisible, {maxWidth:'150px'}, <>
						{('schedule_years' in scheduleInfo) && scheduleInfo.schedule_years.map((schedule_year, index) => {
							const new_year = (scheduleInfo.schedules.findIndex(schedule => schedule.schedule_year_id === schedule_year.schedule_year_id) !== -1) ? false : true;
							const option_selected = (schedule_year.schedule_year_id === schoolInfo.current_schedule_year_id) ? true : false;

							return (
								<React.Fragment key={index}>
								{(index === app_current_schedule_year_index || (index < app_current_schedule_year_index && !new_year)) &&
									<div className={`header-dropdown-content-option ${option_selected && 'header-dropdown-content-option-selected'}`} onClick={option_selected ? null : () => handleSwitchCurrentYear({school_id: schoolInfo.school_id, selected_schedule_year_id: schedule_year.schedule_year_id, new_year:new_year})}>
										<div>{schedule_year.schedule_year}</div>
										{new_year &&
											<div className='green-text' style={{position:'absolute', top:'3px', right:'3px', fontSize:'10px'}}>New Year!</div>
										}
									</div>
								}
								</React.Fragment>
								
							)
						})}
					</>)}
				</div>
				<div className='header-current-year-container header-current-schedule-version-container' onClick={() => handleIconClick('current_schedule_version')} ref={current_schedule_version_ref}>
					<div>{isEmpty(scheduleInfo) ? '--' : current_schedule_version}</div>
					<FontAwesomeIcon className='header-current-year-arrow' icon={faChevronDown} />
					{renderHeaderDropdown(current_schedule_version_ref, currentScheduleVersionOptionsVisible, {maxWidth:'300px'}, <>
						{schedules_from_current_schedule_year.map((schedule, index) => {
							const option_selected = (`${schedule.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);

							return (
								<div className={`header-dropdown-content-option header-schedule-version-option ${option_selected ? 'header-dropdown-content-option-selected' : ''} ${(`${editingScheduleVersionID}` === `${schedule.schedule_version_id}`) ? 'header-schedule-version-option-editing' : ''}`} key={index} onClick={option_selected ? null : () => handleSwitchCurrentScheduleVersion({school_id: schoolInfo.school_id, selected_schedule_version_id: schedule.schedule_version_id, selected_schedule_year_id: schedule.schedule_year_id})}>
									{(`${editingScheduleVersionID}` === `${schedule.schedule_version_id}`) ?
										<div className='header-schedule-version-option-name-editing-container' onClick={(e) => e.stopPropagation()}>
											<input
												ref={schedule_name_input_ref}
												className='header-schedule-version-option-name-input'
												value={editingScheduleName}
												onChange={(e) => setEditingScheduleName(e.target.value)}
												onKeyDown={(e) => {
													if(e.key === 'Enter') saveScheduleNameEdit(e);
													if(e.key === 'Escape') cancelScheduleNameEdit(e);
												}}
											/>
											<FontAwesomeIcon className='header-schedule-version-option-name-action checkmark-submit' icon={faCheck} onClick={saveScheduleNameEdit}/>
											<FontAwesomeIcon className='header-schedule-version-option-name-action x-cancel' icon={faTimes} onClick={cancelScheduleNameEdit}/>
										</div>
										:
										<div className={`header-schedule-version-option-name-editable ${can_manage_schedule_versions ? '' : 'header-schedule-version-option-name-readonly'}`} onClick={(e) => handleScheduleNameClick(e, schedule, option_selected)} onDoubleClick={can_manage_schedule_versions ? (e) => startScheduleNameEdit(e, schedule) : undefined}>
											<div className='header-schedule-version-option-name-label'>
												{schedule.schedule_name}
											</div>
											{can_manage_schedule_versions &&
												<FontAwesomeIcon className='header-schedule-version-option-name-edit-trigger' icon={faEdit} title='Edit schedule name' onClick={(e) => startScheduleNameEdit(e, schedule)}/>
											}
										</div>
									}
									{(`${editingScheduleVersionID}` !== `${schedule.schedule_version_id}`) &&
										<div className='header-schedule-version-option-actions'>
											{can_manage_schedule_versions && can_duplicate_schedule_versions &&
												<div className={`header-schedule-version-action-btn ${duplicate_schedule_limit_reached ? 'header-schedule-version-action-btn-muted' : ''}`} title={duplicate_schedule_limit_reached ? `Maximum of ${max_schedules_allowed} schedules reached for this year` : 'Duplicate schedule'} onClick={(e) => {
													e.stopPropagation();
													handleDuplicateScheduleVersion({school_id: schoolInfo.school_id, schedule_version_id: schedule.schedule_version_id, schedule_year_id: schedule.schedule_year_id, schedule_name: schedule.schedule_name});
												}}>
													<FontAwesomeIcon className='opaque-link' icon={faClone}/>
												</div>
											}
											{can_delete_schedule_versions &&
												<div className='header-schedule-version-action-btn' title='Delete schedule' onClick={(e) => confirmDeleteSchedule(e, schedule)}>
													<FontAwesomeIcon className='opaque-link' icon={faTrash}/>
												</div>
											}
										</div>
									}
								</div>
							)
						})}
						{(schedules_from_current_schedule_year.length === 0) &&
							<div className='header-dropdown-content-option'>
								<div>--</div>
							</div>
						}
					</>)}
				</div>
			</div>
			<div></div>
				<div className='header-dropdown-container' onClick={() => handleIconClick('settings')} ref={settings_ref}>
				<div className="header-account-info-container">
					{!isLoading &&
						<>
						<div className="header-school-info school-name">{schoolInfo.name}</div>
						<div className="header-user-info">
							{(userInfo.last_name && userInfo.first_name) &&
								<><span className="user-lastname">{userInfo.last_name}</span>, <span className="user-firstname">{userInfo.first_name}</span></>
							}
							
						</div>
						</>
					}
					</div>
					<div className="header-dropdown-link" data-contenttype="account"><FontAwesomeIcon style={{fontSize:"25px"}} icon={faUserCircle} /></div>
					{renderHeaderDropdown(settings_ref, settingOptionsVisible, {}, <>
						<Link className="header-dropdown-content-option" to={`/app/select_school`} onClick={() => setLoadScreen('select_school')}>Switch Schools</Link>
						<Link className="header-dropdown-content-option" to={`/app/${schoolType}/documentation/get_started`} target="_blank">Help</Link>
						<Link className="header-dropdown-content-option" to={{pathname:`/app/${schoolType}/settings/account/`, redirect:location.pathname, load_screen:loadScreen}} onClick={() => setLoadScreen('settings')}>Settings</Link>
						<Link className="header-dropdown-content-option sign-out-btn" onClick={signOut} to={`/app/login`}>Sign Out</Link>
					</>)}
				</div>
			</div>
	);
}
